import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useCallback } from "react";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export interface Notification {
  id: string;
  type: 'tat_breach' | 'event';
  activity_id: string;
  patient_id: string;
  workflow_step: string;
  message: string;
  escalation_level?: number;
  elapsed_minutes?: number;
  threshold_minutes?: number;
  alert_type?: string;
  event_type?: string;
  sent_at: string;
  patient_name?: string;
  patient_uhid?: string;
  isRead: boolean;
}

const STORAGE_KEY = 'notifications_read_ids';
const getReadIds = (): Set<string> => {
  try { const stored = localStorage.getItem(STORAGE_KEY); return stored ? new Set(JSON.parse(stored)) : new Set(); } catch { return new Set(); }
};
const saveReadIds = (ids: Set<string>) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids])); } catch {}
};

const formatTATBreachMessage = (escalationLevel: number, workflowStep: string, patientName: string): string => {
  const levelText = escalationLevel === 1 ? 'Level 1' : escalationLevel === 2 ? 'Level 2' : 'Level 3';
  const stepNames: Record<string, string> = {
    'MD': 'Mark for Discharge', 'DSS': 'Discharge Summary Saved', 'DSF': 'Discharge Summary Approval',
    'PC_PHARMACY': 'Pharmacy Clearance', 'CS': 'Charge Sheet', 'BS': 'Bill Summary',
    'BA': 'Bill Approval', 'PC': 'Payment Completed', 'PD': 'Physical Discharge',
  };
  return `${levelText} TAT breach for ${stepNames[workflowStep] || workflowStep} of patient ${patientName}`;
};

export const useNotifications = () => {
  const queryClient = useQueryClient();
  const [readIds, setReadIds] = useState<Set<string>>(getReadIds);
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();

  useEffect(() => { setReadIds(getReadIds()); }, [currentBranchId]);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications", organizationId, currentBranchId],
    queryFn: async () => {
      if (currentBranchId == null || !organizationId) return [];
      const currentReadIds = getReadIds();

      const { data: tatAlerts, error: tatError } = await supabase
        .from("tat_alert_logs").select(`*, patients (name, patient_id)`)
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .order("sent_at", { ascending: false }).limit(100);
      if (tatError) throw tatError;

      const { data: eventNotifications, error: eventError } = await supabase
        .from("event_notifications").select(`*, patients (name, patient_id)`)
        .eq("branch_id", currentBranchId).eq("organization_id", organizationId)
        .order("created_at", { ascending: false }).limit(100);
      if (eventError) throw eventError;

      const tatNotifications: Notification[] = (tatAlerts || []).map((alert) => ({
        id: `tat_${alert.id}`, type: 'tat_breach' as const, activity_id: alert.activity_id,
        patient_id: alert.patient_id, workflow_step: alert.workflow_step,
        message: formatTATBreachMessage(alert.escalation_level, alert.workflow_step, alert.patients?.name || 'Unknown'),
        escalation_level: alert.escalation_level, elapsed_minutes: alert.elapsed_minutes,
        threshold_minutes: alert.threshold_minutes, alert_type: alert.alert_type,
        sent_at: alert.sent_at, patient_name: alert.patients?.name,
        patient_uhid: alert.patients?.patient_id, isRead: currentReadIds.has(`tat_${alert.id}`),
      }));

      const eventNotifs: Notification[] = (eventNotifications || []).map((event) => ({
        id: `event_${event.id}`, type: 'event' as const, activity_id: event.activity_id,
        patient_id: event.patient_id, workflow_step: event.workflow_step,
        message: event.message, event_type: event.event_type,
        sent_at: event.created_at, patient_name: event.patients?.name,
        patient_uhid: event.patients?.patient_id, isRead: currentReadIds.has(`event_${event.id}`),
      }));

      return [...tatNotifications, ...eventNotifs].sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
    },
    enabled: currentBranchId != null && !!organizationId,
    staleTime: 30000,
  });

  useEffect(() => {
    const tatChannel = supabase.channel('tat-alert-logs-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tat_alert_logs' },
        () => { queryClient.invalidateQueries({ queryKey: ["notifications", organizationId, currentBranchId] }); })
      .subscribe();
    const eventChannel = supabase.channel('event-notifications-changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'event_notifications' },
        () => { queryClient.invalidateQueries({ queryKey: ["notifications", organizationId, currentBranchId] }); })
      .subscribe();
    return () => { supabase.removeChannel(tatChannel); supabase.removeChannel(eventChannel); };
  }, [queryClient, organizationId, currentBranchId]);

  const markAsRead = useCallback((notificationId: string) => {
    setReadIds((prev) => { const updated = new Set(prev); updated.add(notificationId); saveReadIds(updated); return updated; });
    queryClient.setQueryData<Notification[]>(["notifications", organizationId, currentBranchId], (old) => {
      if (!old) return old;
      return old.map((n) => n.id === notificationId ? { ...n, isRead: true } : n);
    });
  }, [queryClient, organizationId, currentBranchId]);

  const markAllAsRead = useCallback(() => {
    const allIds = new Set(notifications.map((n) => n.id));
    setReadIds(allIds); saveReadIds(allIds);
    queryClient.setQueryData<Notification[]>(["notifications", organizationId, currentBranchId], (old) => {
      if (!old) return old;
      return old.map((n) => ({ ...n, isRead: true }));
    });
  }, [notifications, queryClient, organizationId, currentBranchId]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  return { notifications, isLoading, unreadCount, markAsRead, markAllAsRead };
};
